import { takesMoney } from "../utils/takesMoney";
import { openMoneyGate } from "../lib/connectBank";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaGlobe, FaQrcode, FaInstagram } from "react-icons/fa";
import NumberPad from "./NumberPad";
import PriceFloorNote from "./PriceFloorNote";
import { belowFloor, liftToFloor } from "../utils/priceFloor";
import CoachBubble from "./CoachBubble";
import { priceHintKind } from "../lib/welcomeHints";
import { formatAmountForCopy } from "../utils/currency";
import { roundPriceStep } from "../utils/roundPriceStep";
import { openExternal } from "../lib/openExternal";
import { useProfileDrawer } from "../lib/profileDrawer";
import { usersApi } from "../services/users";
import type { User } from "../types/User";
import type { VisitCounts } from "../types/orders";
import { absUrl, curSym, formatChipCount, isSuffixCur, normCur, num } from "../utils/requestsFormat";
import { t as tr } from "../i18n";
import { T } from "../i18n/T";
import "./PaintSkeleton.css";
import "./RequestsHeader.css";
import { DEFAULT_AVATAR_SRC, showDefaultAvatar } from "../lib/defaultAvatar";

// The top of the Requests page: avatar (opens the profile drawer), QR badge,
// name + Instagram, the Fans chip, and the request price with its number
// pad. Owns only the pad's open/draft state; the price itself lives on the
// page because the demo cards show it too. Split out of pages/RequestsPage.tsx.
// After the practice cards it also carries the one-time price bubble
// (lib/welcomeHints, founder pick A, build 44); the page decides when it's due.
//
// Nothing pops in late (1.5.2): the page hands over the last-known profile
// (the cache) at once; on the very first open, the signed-in account's own
// name and photo, and a skeleton where the price will land. What a tap DOES
// (the price pad, the money gate) is decided on this visit's server copy.

type Props = {
  /** What to paint: this visit's profile, else the last one seen. */
  profile: User | null;
  /** The signed-in account (name, photo) — paints the first-ever open. */
  account?: Pick<User, "name" | "avatarUrl" | "slug"> | null;
  /** This visit's server copy — null until it lands. Taps act on this. */
  freshProfile: User | null;
  onProfile: (u: User) => void;
  /** Reads the profile now (a tap before this visit's copy landed). */
  refreshProfile: () => Promise<User | null>;
  price: number;
  setPrice: React.Dispatch<React.SetStateAction<number>>;
  visits: VisitCounts | null;
  /** First-ever open, count not back yet: keep its room. */
  visitsLoading?: boolean;
  onOpenFans: () => void;
  /** The one-time price bubble is due (and nothing is on top of the page). */
  coach?: boolean;
  /** The first tap on the price box — or the bubble — ends it for good. */
  onCoachDone?: () => void;
};

export default function RequestsHeader({
  profile,
  account,
  freshProfile,
  onProfile,
  refreshProfile,
  price,
  setPrice,
  visits,
  visitsLoading = false,
  onOpenFans,
  coach = false,
  onCoachDone,
}: Props) {
  // Name + photo: the profile, else the account (first-ever open).
  const shown = profile ?? account ?? null;
  // The price (and Free) are only known once a profile is.
  const priceKnown = !!profile;
  const { open: openProfileDrawer } = useProfileDrawer();
  // Tapping the price opens our shared NumberPad (same keypad as onboarding)
  // instead of the OS keyboard. `priceDraft` is the in-pad buffer; it commits on
  // Done. Starts blank each open (Cash App / Venmo style), so Done-without-typing
  // leaves the current price untouched.
  const [pricePadOpen, setPricePadOpen] = useState<boolean>(false);
  const [priceDraft, setPriceDraft] = useState("");
  // The box the price bubble's arrow points at (state, so the bubble places
  // itself once the box exists).
  const [priceBox, setPriceBox] = useState<HTMLDivElement | null>(null);
  const coachKind = coach ? priceHintKind(profile) : null;

  const handlePriceUpdate = async (explicit?: number) => {
    const p = freshProfile;
    if (!p) return;
    try {
      const target = explicit != null ? explicit : price;
      const fixed = roundPriceStep(num(target), p.currency);
      await usersApi.setPrice(p.slug, fixed);
      const refreshed = await usersApi.get(p.slug);
      onProfile(refreshed);
      setPrice(roundPriceStep(refreshed.pricePerRequest ?? 0, p.currency));
      setPricePadOpen(false);
    } catch (err) {
      console.error("Ошибка обновления цены:", err);
      // The display was updated optimistically in commitPricePad — snap it
      // back to the server's truth so a failed save can't show a wrong price.
      try {
        const refreshed = await usersApi.get(p.slug);
        setPrice(roundPriceStep(refreshed.pricePerRequest ?? 0, p.currency));
      } catch {
        /* offline — leave as-is; next successful fetch re-syncs */
      }
    }
  };

  // FREE TIER: fans of a talent who can't take money yet are asked nothing,
  // so the header says "Free" (never a price nobody is charged) and the tap
  // opens the money gate — the same answer the Profile price card gives.
  const free = !takesMoney(profile);

  // Gate or pad is decided on this visit's profile (fetched now if the tap
  // beat it) — never on the remembered one.
  const openPricePad = async () => {
    if (coachKind) onCoachDone?.();
    const p = freshProfile ?? (await refreshProfile());
    if (!p) return;
    if (!takesMoney(p)) return openMoneyGate(p);
    setPriceDraft(""); // start fresh, like Cash App / Venmo
    setPricePadOpen(true);
  };

  const commitPricePad = () => {
    const t = priceDraft.trim();
    if (t !== "") {
      const n = Number(t.replace(",", "."));
      // 0 => "Request: Free"; under the floor ("Minimum $2" on the pad) → the floor.
      const next = liftToFloor(Number.isFinite(n) ? n : 0, profile?.currency, profile);
      if (roundPriceStep(next, profile?.currency) !== roundPriceStep(num(price), profile?.currency)) {
        // Optimistic: show the new price and close the pad IMMEDIATELY —
        // waiting on the PATCH round-trip used to flash "$0" for a beat.
        // handlePriceUpdate re-syncs from the server (and reverts on failure).
        setPriceDraft("");
        setPrice(roundPriceStep(next, profile?.currency));
        setPricePadOpen(false);
        handlePriceUpdate(next);
        return;
      }
    }
    setPriceDraft("");
    setPricePadOpen(false);
  };

  return (
    <>
      <div className="req-av-block">
        <div className="req-avatar-wrap">
          {/* Tapping your photo opens the profile drawer (from a live test) — the
              same drawer the tab-bar Profile slot opens. */}
          <button
            type="button"
            className="req-avatar-btn"
            onClick={openProfileDrawer}
            aria-label={tr("requestsHeader.openProfile")}
          >
            {shown ? (
              shown.avatarUrl ? (
                <img
                  src={absUrl(shown.avatarUrl)}
                  alt={tr("requestsHeader.avatar")}
                  className="request-avatar-image"
                  onError={showDefaultAvatar}
                />
              ) : (
                // No photo yet: the drawn default picture, not an initial.
                <img
                  src={DEFAULT_AVATAR_SRC}
                  alt={tr("requestsHeader.avatar")}
                  className="request-avatar-image"
                />
              )
            ) : (
              <div className="avatar-placeholder">?</div>
            )}
          </button>
          <Link
            to={`/qr/${shown?.slug || ""}`}
            className="qr-badge"
            title={tr("requestsHeader.viewQrCode")}
          >
            <FaQrcode size={11} />
          </Link>
        </div>

        {/* Compact corner header (founder decision): name + IG on top,
            Fans + price side-by-side beneath, all right of the small avatar —
            the queue starts a full avatar-column higher than the old centered
            stack. */}
        <div className="req-head-main">
          <div className="req-head-name-row">
            <h1 className="user-namen-rq">{shown?.name || ""}</h1>
            {profile?.instagram && (
              <a
                href={`https://instagram.com/${profile.instagram}`}
                className="req-head-ig"
                title={`@${profile.instagram}`}
                aria-label={tr("requestsHeader.instagram")}
                onClick={(e) => {
                  e.preventDefault();
                  openExternal(`https://instagram.com/${profile.instagram}`);
                }}
              >
                <FaInstagram size={13} />
              </a>
            )}
          </div>

          <div className="btn-req-head-a">
            <button
              className="connected-btn fans-chip"
              onClick={onOpenFans}
            >
              <FaGlobe />{tr("requestsHeader.fans")}
              {visits ? (
                <span className="fans-chip-count">· {formatChipCount(visits.d7)}</span>
              ) : (
                visitsLoading && <span className="fans-chip-count psk psk--inline rqh-skel-count" aria-hidden="true" />
              )}
            </button>

            <div className="price-edit-wrapper">
              <div
                ref={setPriceBox}
                className={`price-display${pricePadOpen ? " editing" : ""}${coachKind ? " coach-glow" : ""}`}
                onClick={openPricePad}
              >
                <span className="price-display-label">{tr("requestsHeader.requestPrice")}</span>
                <span className="price-display-text">
                  {pricePadOpen ? (
                    <>
                      {/* While the pad is open: an untouched draft shows the
                          CURRENT price grayed out (never a jarring "0"); the
                          first digit typed replaces it. Blinking cursor =
                          "you're editing". */}
                      {isSuffixCur(profile?.currency) ? "" : curSym(profile?.currency || "usd")}
                      {priceDraft !== "" ? (
                        priceDraft
                      ) : (
                        <span className="price-ghost">
                          {roundPriceStep(num(price), profile?.currency).toFixed(2)}
                        </span>
                      )}
                      {isSuffixCur(profile?.currency) && ` ${curSym(profile?.currency)}`}
                      <span className="price-cursor" aria-hidden="true" />
                    </>
                  ) : !priceKnown ? (
                    <span className="psk psk--inline rqh-skel-price" aria-hidden="true" />
                  ) : free || num(price) === 0 ? (
                    tr("requestsHeader.free")
                  ) : (
                    formatAmountForCopy(
                      roundPriceStep(num(price), profile?.currency),
                      normCur(profile?.currency)
                    )
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
        <NumberPad
          open={pricePadOpen}
          value={priceDraft}
          onChange={setPriceDraft}
          onDone={commitPricePad}
          allowDecimal
          withBackdrop
          maxLength={6}
          note={<PriceFloorNote currency={profile?.currency} talent={profile} below={belowFloor(priceDraft, profile?.currency, profile)} allowFree />}
        />
      </div>
      {coachKind && (
        <CoachBubble anchor={priceBox} onTap={openPricePad}>
          {coachKind === "price" && tr("priceCoach.yourPrice")}
          {coachKind === "bank" && (
            <>
              <T k="priceCoach.connectBank" tags={{ 0: (c) => <b>{c}</b> }} />
              <small>{tr("priceCoach.freeUntilThen")}</small>
            </>
          )}
          {coachKind === "review" && tr("priceCoach.inReview")}
        </CoachBubble>
      )}
    </>
  );
}
